import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import LogoImageDefault from '../assets/logo_img.png';

const defaultLogoPaths = [
     '/assets/2 Logos2.png',
     '/assets/2Logos2.png',
     '/assets/logo.png',
     '/logo.png',
];

// Simplified URL resolver: prefer absolute/data/remote URLs unchanged.
// For relative paths, prefix with envFileUrl || baseUrl || window origin.
const resolveFileUrl = (u, baseUrl, envFileUrl) => {
     if (!u) return null;
     if (/^data:|^https?:\/\//i.test(u)) return u;
     const cleaned = String(u).replace(/^\/+/, '');

     const root = (envFileUrl || baseUrl || (typeof window !== 'undefined' && window.location && `${window.location.protocol}//${window.location.host}`) || '').replace(/\/+$/, '');
     if (!root) return `/${cleaned}`;

     // If the incoming path already contains an uploads/ segment, attach it directly.
     if (/uploads\//i.test(cleaned)) {
          return `${root}/${cleaned}`;
     }

     // Otherwise assume it's a filename (or sign filename) and place under uploads/signs
     const filename = cleaned.replace(/^.*uploads\/signs\//i, '').replace(/^.*uploads\//i, '').replace(/^\/+/, '');
     return `${root}/uploads/signs/${encodeURIComponent(filename)}`;
};

// Simplified loader: try to fetch and convert to data URL; on failure return the
// original URL so callers can use an <img> element (jsPDF accepts HTMLImageElement).
const loadImageAsDataUrl = async (url) => {
     if (!url) return null;
     if (/^data:/i.test(url)) return url;
     const token = localStorage.getItem('token');
     const headers = token ? { Authorization: `Bearer ${token}` } : {};

     // If the URL references an uploads path, try the backend proxy first to avoid CORS.
     try {
          const parsedForProxy = new URL(url, window.location.href);
          const uploadsIdx = parsedForProxy.pathname.indexOf('/uploads/');
          if (uploadsIdx !== -1) {
               const filePath = parsedForProxy.pathname.substring(uploadsIdx + '/uploads/'.length) + (parsedForProxy.search || '');
               const proxyOrigin = parsedForProxy.origin;
               const proxyUrl = `${proxyOrigin}/internalhrms_backend/proxy-image.php?file=${encodeURIComponent(filePath)}`;
               try {
                    const pRes = await fetch(proxyUrl, { method: 'GET', mode: 'cors', headers });
                    try { console.debug('loadImageAsDataUrl: forced proxy fetch', { proxyUrl, status: pRes.status, ok: pRes.ok }); } catch (e) { }
                    if (pRes && pRes.ok) {
                         const blobP = await pRes.blob();
                         return await new Promise((resolve) => {
                              const fr = new FileReader();
                              fr.onloadend = () => resolve(fr.result);
                              fr.readAsDataURL(blobP);
                         });
                    }
               } catch (e) {
                    /* proxy attempt failed — fall through to direct fetch */
               }
          }
     } catch (e) { /* ignore URL parsing errors */ }

     try {
          const res = await fetch(url, { method: 'GET', mode: 'cors', headers });
          try { console.debug('loadImageAsDataUrl: fetch', { url, status: res.status, ok: res.ok, headers: { acao: res.headers.get('access-control-allow-origin'), acah: res.headers.get('access-control-allow-headers'), acam: res.headers.get('access-control-allow-methods') } }); } catch (e) { }
          if (res && res.ok) {
               const blob = await res.blob();
               return await new Promise((resolve) => {
                    const fr = new FileReader();
                    fr.onloadend = () => resolve(fr.result);
                    fr.readAsDataURL(blob);
               });
          } else {
               // when non-ok, try to surface reason in console for easier debugging
               try {
                    const txt = await res.text();
                    console.debug('loadImageAsDataUrl: non-ok response', { url, status: res.status, text: txt });
               } catch (e) { /* ignore */ }
          }
     } catch (e) {
          try { console.debug('loadImageAsDataUrl: fetch failed', { url, err: e && (e.message || e) }); } catch (ex) { }
          // fetch failed (CORS or network). Fall through to return original URL.
     }

     // If fetch failed (likely CORS), attempt to use the backend proxy if the URL contains '/uploads/'
     try {
          const parsed = new URL(url, window.location.href);
          const uploadsIndex = parsed.pathname.indexOf('/uploads/');
          if (uploadsIndex !== -1) {
               const filePath = parsed.pathname.substring(uploadsIndex + '/uploads/'.length) + (parsed.search || '');
               const proxyOrigin = parsed.origin; // use same origin as image host
               const proxyUrl = `${proxyOrigin}/internalhrms_backend/proxy-image.php?file=${encodeURIComponent(filePath)}`;
               try {
                    const pres = await fetch(proxyUrl, { method: 'GET', mode: 'cors', headers });
                    try { console.debug('loadImageAsDataUrl: proxy fetch', { proxyUrl, status: pres.status, ok: pres.ok }); } catch (e) { }
                    if (pres && pres.ok) {
                         const blob = await pres.blob();
                         return await new Promise((resolve) => {
                              const fr = new FileReader();
                              fr.onloadend = () => resolve(fr.result);
                              fr.readAsDataURL(blob);
                         });
                    }
               } catch (e) { /* proxy fetch failed */ }
          }
     } catch (e) { /* ignore URL parse errors */ }

     // Final fallback: return the URL so the caller can set img.src = url
     // and let the browser render the image (jsPDF can accept an HTMLImageElement).
     return url;
};

// Export helpers so other modules can prefetch signatures before calling PDF exporter
export { resolveFileUrl, loadImageAsDataUrl };

// NOTE: images must be fetched directly from server (no proxy fallback) — keep simple loader only

export async function exportRenewalToPDF({ renewal = {}, details = [], id = '', baseUrl = '', publicUrl = '', logoImage = LogoImageDefault, envFileUrl = '', outputMode = 'open', prefetchedSigDataUrls: prefetchedSigDataUrlsParam = null, prefetchedRawSigUrls: prefetchedRawSigUrlsParam = null } = {}) {
     const headers = ['Sr. No', 'Task', 'Points', 'HOD Points', 'Comments', 'HOD Comments'];

     // ensure question_text available
     let mappedDetails = details || [];
     const needsText = Array.isArray(mappedDetails) && mappedDetails.some(d => !d.question_text && (d.question_id || d.id));
     if (needsText && baseUrl) {
          try {
               const token = localStorage.getItem('token');
               const headersReq = { Accept: 'application/json' };
               if (token) headersReq.Authorization = `Bearer ${token}`;
               const qUrl = `${baseUrl}/getConsultantRenewalQuestions`;
               const qres = await fetch(qUrl, { method: 'GET', headers: headersReq });
               const qjson = await qres.json();
               if (Array.isArray(qjson)) {
                    const map = qjson.reduce((acc, q) => ({ ...acc, [String(q.id)]: q.question_text || q.question || '' }), {});
                    mappedDetails = mappedDetails.map(d => ({ ...d, question_text: d.question_text || map[String(d.question_id)] || map[String(d.id)] || '' }));
               }
          } catch (err) {
               console.debug('Failed to fetch questions for PDF export:', err);
          }
     }

     const rows = Array.isArray(mappedDetails)
          ? mappedDetails.map((d, idx) => ([idx + 1, d.question_text || d.question || d.question_label || d.question_id || '', d.points || '', d.admin_points || '', d.comments || '', d.admin_comments || '']))
          : [];

     // prepare prefetched signature arrays (may be passed by caller)
     let prefetchedSigDataUrls = [null, null];
     let prefetchedRawSigUrls = [null, null];
     try {
          const hasPrefetched = Array.isArray(prefetchedSigDataUrlsParam) && prefetchedSigDataUrlsParam.length === 2 && prefetchedSigDataUrlsParam.some(v => !!v);
          if (hasPrefetched) {
               prefetchedSigDataUrls = prefetchedSigDataUrlsParam;
               prefetchedRawSigUrls = (Array.isArray(prefetchedRawSigUrlsParam) && prefetchedRawSigUrlsParam.length === 2 && prefetchedRawSigUrlsParam.some(v => !!v)) ? prefetchedRawSigUrlsParam : [null, null];
          } else {
               const fetchSignFor = async (code) => {
                    if (!code) return null;
                    const tried = [];
                    const token = localStorage.getItem('token');
                    const headersReq = { Accept: 'application/json' };
                    if (token) headersReq.Authorization = `Bearer ${token}`;

                    const tryFetchUrl = async (fetchUrl) => {
                         try {
                              tried.push(fetchUrl);
                              const res = await fetch(fetchUrl, { method: 'GET', headers: headersReq });
                              if (!res.ok) return null;
                              const json = await res.json();
                              const arr = Array.isArray(json) ? json : (json && Array.isArray(json.data) ? json.data : []);
                              if (!arr || !arr.length) return null;
                              let match = arr.find(s => String(s.emp_code) === String(code) || String(s.emp_code) === String(code).replace(/^0+/, ''));
                              if (!match) {
                                   const needle = String(code);
                                   match = arr.find(s => String(s.file_url || s.file || s.url || '').includes(needle));
                              }
                              if (!match) return null;
                              const raw = match.file_url || match.file || match.url || null;
                              if (!raw) return null;
                              const finalUrl = (/^https?:\/\//i.test(String(raw))) ? raw : resolveFileUrl(raw, baseUrl, envFileUrl);
                              return finalUrl;
                         } catch (err) {
                              return null;
                         }
                    };

                    const base = baseUrl ? String(baseUrl).replace(/\/$/, '') : '';
                    const candidates = [];
                    if (base) {
                         candidates.push(`${base}/consultantSigns?emp_code=${encodeURIComponent(code)}`);
                         candidates.push(`${base}/api/consultantSigns?emp_code=${encodeURIComponent(code)}`);
                         candidates.push(`${base.replace(/\/api(?:\/v\d+)?$/i, '')}/consultantSigns?emp_code=${encodeURIComponent(code)}`);
                         candidates.push(`${base.replace(/\/api(?:\/v\d+)?$/i, '')}/api/consultantSigns?emp_code=${encodeURIComponent(code)}`);
                    }
                    if (envFileUrl) {
                         const envRoot = String(envFileUrl).replace(/\/$/, '');
                         candidates.push(`${envRoot}/consultantSigns?emp_code=${encodeURIComponent(code)}`);
                         candidates.push(`${envRoot}/api/consultantSigns?emp_code=${encodeURIComponent(code)}`);
                    }
                    if (typeof window !== 'undefined' && window.location) {
                         const hostRoot = `${window.location.protocol}//${window.location.host}`;
                         candidates.push(`${hostRoot}/consultantSigns?emp_code=${encodeURIComponent(code)}`);
                         candidates.push(`${hostRoot}/api/consultantSigns?emp_code=${encodeURIComponent(code)}`);
                    }

                    const uniq = Array.from(new Set(candidates.filter(Boolean)));
                    for (const u of uniq) {
                         const found = await tryFetchUrl(u);
                         if (found) return found;
                    }
                    return null;
               };

               const selfCode = renewal?.emp_code || renewal?.empCode || null;
               const adminCode = renewal?.admin_emp_code || renewal?.admin?.emp_code || renewal?.admin?.empCode || null;
               const [selfUrl, adminUrl] = await Promise.all([fetchSignFor(selfCode), fetchSignFor(adminCode)]);
               const resolvedSelf = selfUrl ? (resolveFileUrl(selfUrl, baseUrl, envFileUrl) || selfUrl) : null;
               const resolvedAdmin = adminUrl ? (resolveFileUrl(adminUrl, baseUrl, envFileUrl) || adminUrl) : null;
               prefetchedRawSigUrls[0] = resolvedSelf;
               prefetchedRawSigUrls[1] = resolvedAdmin;
               prefetchedSigDataUrls[0] = resolvedSelf ? await loadImageAsDataUrl(resolvedSelf) : null;
               prefetchedSigDataUrls[1] = resolvedAdmin ? await loadImageAsDataUrl(resolvedAdmin) : null;
          }
     } catch (err) {
          try { console.debug('exportRenewalToPDF: signature prefetch failed', err); } catch (e) { }
     }

     const doc = new jsPDF('p', 'pt', 'a4');
     const pageWidth = doc.internal.pageSize.getWidth();
     const marginLeft = 40;
     const marginRight = 40;
     const topMargin = 72;
     const title = `renewal_${renewal?.emp_code || id}`;

     doc.setFont('helvetica', 'bold');
     doc.setFontSize(9);
     const titleText = `Consultant renewal: ${renewal.emp_name || renewal.comp_name || ''}`;
     const empCodeText = `Emp Code: ${renewal.emp_code || 'N/A'}`;

     let contentTop;
     let logoDrawn = false;

     // prefer bundler logo if present
     if (logoImage) {
          try {
               const imgEl = new Image();
               try { imgEl.crossOrigin = 'Anonymous'; } catch (e) { }
               const loaded = await new Promise((resolve) => {
                    imgEl.onload = () => resolve(true);
                    imgEl.onerror = () => resolve(false);
                    imgEl.src = logoImage;
               });
               if (loaded) {
                    const maxLogoWidth = 150;
                    const maxLogoHeight = 70;
                    const ratio = Math.min(maxLogoWidth / imgEl.naturalWidth, maxLogoHeight / imgEl.naturalHeight, 1);
                    const drawW = imgEl.naturalWidth * ratio;
                    const drawH = imgEl.naturalHeight * ratio;
                    const logoX = marginLeft;
                    const logoY = topMargin - drawH + 6;
                    const canvas = document.createElement('canvas');
                    canvas.width = imgEl.naturalWidth;
                    canvas.height = imgEl.naturalHeight;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(imgEl, 0, 0);
                    const dataUrl = canvas.toDataURL();
                    let logoType = 'PNG';
                    try {
                         const mime = (dataUrl.split(',')[0].match(/data:(.*?);/) || [])[1] || '';
                         if (mime.toLowerCase().includes('jpeg') || mime.toLowerCase().includes('jpg')) logoType = 'JPEG';
                         else if (mime.toLowerCase().includes('png')) logoType = 'PNG';
                    } catch (e) { }
                    doc.addImage(dataUrl, logoType, logoX, logoY, drawW, drawH);
                    const logoBottom = logoY + drawH;
                    contentTop = logoBottom + 20;
                    logoDrawn = true;
               }
          } catch (e) { }
     }

     if (!logoDrawn) {
          const logoPaths = defaultLogoPaths.concat(publicUrl ? [`${publicUrl}/assets/2 Logos2.png`, `${publicUrl}/assets/2Logos2.png`] : []);
          for (const lp of logoPaths) {
               try {
                    const r = await fetch(encodeURI(lp));
                    if (!r.ok) continue;
                    const blob = await r.blob();
                    const dataUrl = await new Promise((resolve) => {
                         const fr = new FileReader();
                         fr.onloadend = () => resolve(fr.result);
                         fr.readAsDataURL(blob);
                    });
                    const img = new Image();
                    try { img.crossOrigin = 'Anonymous'; } catch (e) { }
                    const loaded = await new Promise((resolve) => {
                         img.onload = () => resolve(true);
                         img.onerror = () => resolve(false);
                         img.src = dataUrl;
                    });
                    if (!loaded) continue;
                    const maxLogoWidth = 150;
                    const maxLogoHeight = 70;
                    const ratio = Math.min(maxLogoWidth / img.width, maxLogoHeight / img.height, 1);
                    const drawW = img.width * ratio;
                    const drawH = img.height * ratio;
                    const logoX = marginLeft;
                    const logoY = topMargin - drawH + 6;
                    doc.addImage(dataUrl, 'PNG', logoX, logoY, drawW, drawH);
                    const logoBottom = logoY + drawH;
                    contentTop = logoBottom + 20;
                    logoDrawn = true;
                    break;
               } catch (err) {
                    // try next
               }
          }
     }

     const titleY = (typeof contentTop !== 'undefined') ? contentTop : topMargin;
     const empY = titleY + 14;
     doc.text(titleText, marginLeft, titleY);
     doc.text(empCodeText, marginLeft, empY);

     doc.setFontSize(9);
     let metaY = titleY + 28;
     const usableWidth = pageWidth - marginLeft - marginRight;
     const col1 = marginLeft;
     const col2 = marginLeft + Math.round(usableWidth * 0.33);
     const col3 = marginLeft + Math.round(usableWidth * 0.66);

     const drawLabelValue = (x, y, label, value) => {
          const lbl = `${label}: `;
          const val = (value === undefined || value === null || value === '') ? 'N/A' : String(value);
          doc.setFont('helvetica', 'normal');
          doc.text(lbl, x, y);
          const lblWidth = (typeof doc.getTextWidth === 'function') ? doc.getTextWidth(lbl) : lbl.length * 6;
          doc.setFont('helvetica', 'bold');
          doc.text(val, x + lblWidth + 6, y);
     };

     const formatDateTime = (s) => {
          if (!s) return '';
          try {
               let ds = String(s).trim();
               if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(ds)) ds = ds.replace(' ', 'T');
               const d = new Date(ds);
               if (!isNaN(d.getTime())) {
                    const dd = String(d.getDate()).padStart(2, '0');
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    const hh = String(d.getHours()).padStart(2, '0');
                    const min = String(d.getMinutes()).padStart(2, '0');
                    return `${dd}-${mm}-${yyyy}` + (hh && min ? ` ${hh}:${min}` : '');
               }
          } catch (e) { }
          return String(s);
     };

     // format date-only (DD-MM-YYYY) — used for DOJ display to avoid showing time
     const formatDateOnly = (s) => {
          if (!s) return '';
          try {
               let ds = String(s).trim();
               if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(ds)) ds = ds.replace(' ', 'T');
               const d = new Date(ds);
               if (!isNaN(d.getTime())) {
                    const dd = String(d.getDate()).padStart(2, '0');
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    return `${dd}-${mm}-${yyyy}`;
               }
          } catch (e) { }
          return String(s).split('T')[0] || String(s);
     };

     // normalize DOJ and VDC experience
     const dojRaw = renewal && (renewal.doj || renewal.date_of_joining || renewal.join_date || renewal.joined_on || renewal.joinedOn || '');
     const doj = formatDateOnly(dojRaw);
     const vdcRaw = renewal && (renewal.vdc_exp || renewal.vdc_experience || renewal.vdcExp || '');
     const vdcExp = vdcRaw || '';

     // Row 1: Specialty | Centre | Mobile

     // show Specialty if it exists, otherwise hide

     drawLabelValue(col1, metaY, 'Branch', renewal.centre || '');
     drawLabelValue(col2, metaY, 'Mobile', renewal.mobile || '');
     metaY += 14;
     // Row 2:   Year | Employment Type    
     drawLabelValue(col1, metaY, 'Year', renewal.year || '');
     drawLabelValue(col2, metaY, 'Employment Type', renewal.employment_type || '');
     drawLabelValue(col3, metaY, 'DOJ', doj || '');
     metaY += 14;
     // Row 3: Timing | DOJ | VDC Exp  
     drawLabelValue(col1, metaY, 'VDC Exp', vdcExp);
     if (renewal.timing) {
          drawLabelValue(col2, metaY, 'Timing', renewal.timing || 'N/A');
     }
     if (renewal.specialty) {
          drawLabelValue(col3, metaY, 'Specialty', renewal.specialty);
     }
     metaY += 14;

     // NOTE: overall remarks will be rendered after the questions table

     autoTable(doc, {
          head: [headers],
          body: rows,
          startY: metaY,
          margin: { left: marginLeft, right: marginRight },
          styles: { fontSize: 7, cellPadding: 4 },
          headStyles: { fillColor: [41, 128, 185], textColor: 255 },
          theme: 'striped',
     });

     const displayTotal = Object.values(mappedDetails || {}).reduce((s, it) => s + (parseInt(it.points, 10) || 0), 0);
     const displayAdminTotal = Object.values(mappedDetails || {}).reduce((s, it) => s + (parseInt(it.admin_points, 10) || 0), 0);

     const extraGap = 20;
     const finalY = doc.lastAutoTable ? (doc.lastAutoTable.finalY + 14 + extraGap) : (metaY + rows.length * 12 + 14 + extraGap);

     // Render Overall Remarks (moved to after the questions table)
     try {
          const oc = String(renewal?.overall_comment || '').trim();
          if (oc) {
               const wrap = doc.splitTextToSize(oc, usableWidth);
               const ocY = finalY - 6; // place slightly above totals
               doc.setFont('helvetica', 'bold');
               doc.setFontSize(9);
               doc.text('Overall Remarks:', marginLeft, ocY);
               doc.setFont('helvetica', 'normal');
               doc.setFontSize(8);
               doc.text(wrap, marginLeft + 6, ocY + 12);
          }
     } catch (e) { }

     doc.setFontSize(9);
     doc.setFont('helvetica', 'bold');
     const selfEvalText = `Self renewal Points: ${displayTotal}`;
     // if displayAdminTotal is zero then don't show admin points (to avoid confusion)
     const adminEvalText = `Total HOD Points: ${displayAdminTotal}`;
     doc.text(selfEvalText, pageWidth - marginRight, finalY - 6, { align: 'right' });
     if (parseInt(displayAdminTotal, 10) > 0) {
          doc.text(adminEvalText, pageWidth - marginRight, finalY + 6, { align: 'right' });
     }
     doc.setFont('helvetica', 'normal');

     // Signatures
     const sigTop = finalY + 40;
     const sigAreaWidth = pageWidth - marginLeft - marginRight;
     const sigCols = 2;
     const sigColW = Math.floor(sigAreaWidth / sigCols);
     const sigHeightMax = 40;

     try {
          // Debug: log renewal/signature inputs to help diagnose missing admin data
          try { console.log('exportRenewalToPDF: signatures input', { id, renewal, baseUrl, envFileUrl }); } catch (e) { }

          // Use only the prefetched results resolved by emp_code/admin_emp_code.
          // Prefer renewal-provided sign objects if present
          try {
               if (renewal && renewal.sign && renewal.sign.file_url) {
                    const final = (/^https?:\/\//i.test(String(renewal.sign.file_url))) ? renewal.sign.file_url : resolveFileUrl(renewal.sign.file_url, baseUrl, envFileUrl);
                    try { console.debug('exportRenewalToPDF: using renewal.sign ->', final); } catch (e) { }
                    prefetchedRawSigUrls[0] = final;
                    prefetchedSigDataUrls[0] = await loadImageAsDataUrl(final);
               }
               if (renewal && renewal.admin_sign && renewal.admin_sign.file_url) {
                    const finalA = (/^https?:\/\//i.test(String(renewal.admin_sign.file_url))) ? renewal.admin_sign.file_url : resolveFileUrl(renewal.admin_sign.file_url, baseUrl, envFileUrl);
                    try { console.debug('exportRenewalToPDF: using renewal.admin_sign ->', finalA); } catch (e) { }
                    prefetchedRawSigUrls[1] = finalA;
                    prefetchedSigDataUrls[1] = await loadImageAsDataUrl(finalA);
               }
          } catch (e) { /* ignore */ }

          // prefetchedRawSigUrls contains resolved URLs (or null), prefetchedSigDataUrls contains data URLs or plain URLs.
          let dataUrls = prefetchedSigDataUrls && prefetchedSigDataUrls.length === 2 ? prefetchedSigDataUrls : null;
          const rawResolved = prefetchedRawSigUrls && prefetchedRawSigUrls.length === 2 ? prefetchedRawSigUrls : [null, null];
          if (dataUrls && Array.isArray(dataUrls)) {
               try { console.log('exportRenewalToPDF: using prefetched signature dataUrls', dataUrls, 'rawUrls', rawResolved); } catch (e) { }
          } else {
               const sigUrls = rawResolved;
               try { console.log('exportRenewalToPDF: resolvedSigUrls', sigUrls); } catch (e) { }
               const dataUrlsLoaded = await Promise.all(sigUrls.map(u => (u ? loadImageAsDataUrl(u) : null)));
               try { console.log('exportRenewalToPDF: loadedDataUrls', dataUrlsLoaded); } catch (e) { }
               dataUrls = dataUrlsLoaded;
          }
          // derive admin name/code fallbacks (used when prefetched values missing)
          const adminNameFallback = renewal?.admin_name || (renewal.admin && (renewal.admin.name || renewal.admin.emp_name)) || localStorage.getItem('user_name') || localStorage.getItem('userName') || '';
          const adminCodeFallback = renewal?.admin_emp_code || (renewal.admin && (renewal.admin.emp_code || renewal.admin.empCode)) || localStorage.getItem('user_code') || localStorage.getItem('userCode') || '';
          const sigNames = [renewal?.emp_name || '', adminNameFallback];
          const sigEmpCodes = [renewal?.emp_code || '', adminCodeFallback];
          const sigColumnLabels = ['Self Renewal By', 'Renewal Reviewed By'];

          for (let i = 0; i < sigCols; i++) {
               const colX = marginLeft + (i * sigColW) + 10;
               const imgData = dataUrls[i];
               const labelText = sigColumnLabels[i] || '';
               const isLeft = (i === 0);
               const padding = 4;
               if (imgData) {
                    const img = new Image();
                    try { img.crossOrigin = 'Anonymous'; } catch (e) { }
                    const loaded = await new Promise((resolve) => {
                         img.onload = () => resolve(true);
                         img.onerror = () => resolve(false);
                         img.src = imgData;
                    });
                    if (loaded) {
                         const fixedSigH = sigHeightMax;
                         let drawH = fixedSigH;
                         let drawW = Math.round(img.width * (drawH / img.height));
                         const maxW = sigColW - 30;
                         if (drawW > maxW) { drawW = maxW; drawH = Math.round(img.height * (drawW / img.width)); }
                         // left column -> align image to left; right column -> align image to right
                         const imgX = isLeft ? (colX + padding) : (colX + sigColW - drawW - padding);
                         const imgY = sigTop + Math.floor((fixedSigH - drawH) / 2);
                         try {
                              if (labelText) {
                                   const labelX = isLeft ? colX : (colX + sigColW - padding);
                                   const labelAlign = isLeft ? 'left' : 'right';
                                   doc.text(labelText, labelX, sigTop - 6, { align: labelAlign });
                              }
                         } catch (e) { }
                         // determine image type: prefer data: URL mime, otherwise infer from filename
                         let imgType = 'PNG';
                         try {
                              if (typeof imgData === 'string' && imgData.startsWith('data:')) {
                                   const mime = (imgData.split(',')[0].match(/data:(.*?);/) || [])[1] || '';
                                   if (mime.toLowerCase().includes('jpeg') || mime.toLowerCase().includes('jpg')) imgType = 'JPEG';
                                   else if (mime.toLowerCase().includes('png')) imgType = 'PNG';
                              } else if (typeof imgData === 'string') {
                                   const ext = (img.src || imgData).split('.').pop().toLowerCase();
                                   if (ext.indexOf('jpg') >= 0 || ext.indexOf('jpeg') >= 0) imgType = 'JPEG';
                                   else if (ext.indexOf('png') >= 0) imgType = 'PNG';
                              }
                         } catch (e) { }
                         // pass the HTMLImageElement to jsPDF (avoids re-fetching when possible)
                         try { doc.addImage(img, imgType, imgX, imgY, drawW, drawH); } catch (e) { doc.addImage(imgData, imgType, imgX, imgY, drawW, drawH); }
                         const labelY = sigTop + fixedSigH + 8;
                         doc.setFontSize(9); doc.setFont('helvetica', 'bold');
                         const nameText = sigNames[i] || '';
                         const nameX = isLeft ? imgX : (imgX + drawW);
                         const nameAlign = isLeft ? 'left' : 'right';
                         doc.text(nameText, nameX, labelY, { align: nameAlign });
                    }
               } else {
                    const labelY = sigTop + sigHeightMax + 8;
                    try {
                         if (labelText) {
                              const labelX = isLeft ? colX : (colX + sigColW - padding);
                              const labelAlign = isLeft ? 'left' : 'right';
                              doc.setFontSize(9);
                              doc.text(labelText, labelX, sigTop - 6, { align: labelAlign });
                         }
                    } catch (e) { }
                    if (sigNames[i]) {
                         const nameX = isLeft ? colX : (colX + sigColW - padding);
                         const nameAlign = isLeft ? 'left' : 'right';
                         doc.setFontSize(9); doc.setFont('helvetica', 'bold');
                         doc.text(sigNames[i], nameX, labelY, { align: nameAlign });
                         doc.setFont('helvetica', 'normal');
                         if (sigEmpCodes[i]) doc.text(`Emp Code: ${sigEmpCodes[i]}`, nameX, labelY + 10, { align: nameAlign });
                    }
               }
          }
     } catch (e) {
          console.log('Signature rendering failed for PDF export:', e);
     }

     // Output handling:
     // - 'download' (default): trigger file download via jspdf.save()
     // - 'open': open PDF in new tab (does not auto-download)
     // - 'both': open in new tab and trigger download
     try {
          if (outputMode === 'open' || outputMode === 'both') {
               const blob = doc.output('blob');
               const url = URL.createObjectURL(blob);
               // open in new tab (preferred; does not force download)
               try { window.open(url, '_blank'); } catch (e) { console.debug('Failed to open new tab for PDF', e); }
               // trigger download only if explicitly requested
               if (outputMode === 'both') {
                    try { doc.save(`${title}.pdf`); } catch (e) { console.debug('Failed to save PDF after opening', e); }
               }
               return { blob, url };
          } else if (outputMode === 'download') {
               // explicit download request
               doc.save(`${title}.pdf`);
               return { saved: true };
          } else {
               // unknown mode: default to returning blob and URL without forcing download
               const blob = doc.output('blob');
               const url = URL.createObjectURL(blob);
               try { window.open(url, '_blank'); } catch (e) { console.debug('Failed to open new tab for PDF', e); }
               return { blob, url };
          }
     } catch (e) {
          // On error, return an object describing the failure and any generated blob/url
          try {
               const blob = doc.output && typeof doc.output === 'function' ? doc.output('blob') : null;
               const url = blob ? URL.createObjectURL(blob) : null;
               if (url) try { window.open(url, '_blank'); } catch (ex) { /* ignore */ }
               return { error: e, blob, url };
          } catch (ex) {
               console.debug('PDF output failed', ex);
               return { error: e };
          }
     }
}

export default exportRenewalToPDF;
